import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import '../../styles/overview.scss'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import TextInput from '../../../widgets/components/Input/TextInput'
import { Col, Modal, Row } from 'react-bootstrap'
import { Formik } from 'formik'
import TagifyInput from '../../../shared-access/components/tagify-input'
import { ToolTipUIForModal } from '../../../widgets/components/UI/ToolTipUIForModal'
import { createReport, getAllUsers, getTemplates } from '../../core/_requests'
import { useAuth } from '../../../auth'
import { toast } from 'react-toastify'
import SelectSearchInput from '../../../widgets/components/Input/SelectSearchInput'
import * as Yup from 'yup'
import TagInput from '../../../widgets/components/Input/TagInput'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const GenerateReportExistingTemplate = ({
  setIsActive,
  existingTemplateId,
  setTabKey,
  getAllTemplate,
}: {
  setIsActive: (isActive: boolean) => void
  existingTemplateId?: string
  setTabKey: (key: number) => void
  getAllTemplate: any
}) => {
  const today = new Date()
  const defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1)
  const { companyId } = useAuth()
  const { formatMessage } = useIntl()
  const [, setLoading] = useState<boolean>(false)
  const [templateOptions, setTemplateOptions] = useState<any>([])
  const [, setTemplateData] = useState<any>([])
  const [templateId, setTemplateId] = useState<string>('')
  const [scheduleReportData, setScheduleReportData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [, setEmailList] = useState<any[]>([])
  const [selectedOption, setSelectedOption] = useState<string>('MTD')
  const [, setEmailListLoading] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([defaultStartDate, today])
  const [startDate, endDate] = dateRange

  const getAllEmailList = async () => {
    setEmailListLoading(true)
    if (!companyId) return
    try {
      const {
        data: { data, success },
      } = await getAllUsers(companyId)
      if (success) {
        const formattedData = data.map((item: any) => ({
          id: item.userId,
          value: item.name,
          name: item.name,
        }))

        setEmailList(formattedData)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setEmailListLoading(false)
    }
  }

  useEffect(() => {
    let start: Date | null = null
    let end: Date | null = today

    switch (selectedOption) {
      case 'MTD':
        start = new Date(today.getFullYear(), today.getMonth(), 1)
        break
      case 'QTD':
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3
        start = new Date(today.getFullYear(), quarterStartMonth, 1)
        break
      case 'CYTD':
        start = new Date(today.getFullYear(), 0, 1)
        break
      case 'FYTD':
        const financialYearStart =
          today.getMonth() >= 3
            ? new Date(today.getFullYear(), 3, 1)
            : new Date(today.getFullYear() - 1, 3, 1)
        start = financialYearStart
        break
      case 'custom':
        start = null
        end = null
        break
      default:
        start = null
    }

    setDateRange([start, end])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption])

  useEffect(() => {
    getAllEmailList()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = {
    reportTemplate: scheduleReportData?.recordTemplateId
      ? {
        id: scheduleReportData.recordTemplateId,
        label: scheduleReportData.name,
        value: scheduleReportData.name,
      }
      : { id: '', label: '', value: '' },

    reportName: '',
    AccessActivity: scheduleReportData?.reportType || '',
    recordAccess: scheduleReportData?.recordAccess || false,
    permissionChanges: scheduleReportData?.permissionChanges || false,
    unauthorized: scheduleReportData?.unauthorized || false,
    linkSharing: scheduleReportData?.linkSharing || false,
    geoLocation: scheduleReportData?.geolocation || false,
    validUsers: scheduleReportData?.listOfUser || '',
    departmentLists: Array.isArray(scheduleReportData?.departments)
      ? scheduleReportData.departments.map((option: any) => ({
        id: option?.levelOneId || '',
        label: option?.name || '',
        value: option?.id || '',
      }))
      : [],
    categoryLists: Array.isArray(scheduleReportData?.recordClassificationLevelTwos)
      ? scheduleReportData?.recordClassificationLevelTwos?.map((option: any) => ({
        id: option?.levelTwoId || '',
        label: option?.name || '',
        value: option?.id || '',
      }))
      : [],
    format: scheduleReportData?.format || '',
  }

  const formatTitleCase = (str: string) => {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const formatDate = (date: Date | null) => (date ? date.toISOString().split('T')[0] : '')

  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      if (!companyId || !scheduleReportData?.recordTemplateId) return
      setIsLoading(true)
      const payload = {
        name: values?.reportName,
        templateId: scheduleReportData?.recordTemplateId,
        reportType: formatTitleCase(values?.AccessActivity),
        recordAccess: values?.recordAccess,
        permissionChanges: values?.permissionChanges,
        unauthorized: values?.unauthorized,
        linkSharing: values?.linkSharing,
        geoLocation: values?.geoLocation,
        format: values?.format,
        listOfUser: scheduleReportData?.listOfUser,
        departmentList: values?.departmentLists.map((department: any) => department.id),
        categoryList: values?.categoryLists.map((category: any) => category.id),
        period: selectedOption,
        dateRange: `${formatDate(startDate)},${formatDate(endDate)}`,
      }

      const {
        data: { success, errors },
      } = await createReport(companyId, payload)
      if (success) {
        toast.success('Report created successfully')
        resetForm()
        setIsActive(false)
        setTabKey(1)
        getAllTemplate()
        setScheduleReportData(null)
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  const getAllTemplates = async () => {
    if (!companyId) return
    setLoading(true)
    const type = 'Template'
    const all = true
    try {
      const {
        data: { data, success, errors },
      } = await getTemplates(companyId, type, all)
      if (success) {
        setTemplateData(data?.data)
        setTemplateOptions(
          data?.data?.map((option: any) => ({
            id: option.recordTemplateId,
            label: option.name,
            value: option.name,
          }))
        )
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAllTemplates()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getSingleTemplates = async () => {
    if (!companyId || (!templateId && !existingTemplateId)) return
    setLoading(true)
    const type = 'Template'
    const all = false
    try {
      const {
        data: { data, success, errors },
      } = await getTemplates(
        companyId,
        type,
        all,
        undefined,
        undefined,
        undefined,
        undefined,
        templateId || existingTemplateId
      )
      if (success) {
        setScheduleReportData(data?.data[0])
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (companyId && (templateId || existingTemplateId)) {
      getSingleTemplates()
    }
  }, [companyId, templateId, existingTemplateId]) // eslint-disable-line react-hooks/exhaustive-deps

  const userSchema = Yup.object().shape({
    reportTemplate: Yup.object()
      .shape({
        id: Yup.string().required('Template ID is required'),
        label: Yup.string().required('Template name is required'),
        value: Yup.string().required('Template value is required'),
      })
      .nullable()
      .required('Template name is required'),
    reportName: Yup.string().nullable().required('Activity Type is required'),
  })

  return (
    <>
      <div className='mt-4'>
        <InfoCard2
          title={'Generate Reports Using Predefined Templates'}
          desc={
            'This section allows you to quickly generate reports based on existing templates. Select a template and customize the report details such as actions, users, departments, and categories. You can choose to export reports in PDF or XLS format for further analysis and distribution. Use this feature for fast and easy reporting with predefined configurations.'
          }
          slug={
            'https://help.recordskeeper.ai/en/articles/10374124-how-to-manage-and-access-your-reports-in-recordskeeper-ai'
          }
        />
      </div>

      <div className='rounded-2 mt-6'>
        <h2 className='font-size-16'>
          {formatMessage({ id: 'Generate Report using existing template' })}
        </h2>

        <div className='mt-4'>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={userSchema}
            validateOnChange
            validateOnMount
          >
            {(formik) => {
              setTemplateId(formik?.values?.reportTemplate?.id)
              return (
                <>
                  <>
                    <Row>
                      <Col md={6}>
                        <SelectSearchInput
                          label={formatMessage({ id: 'Select Template' })}
                          fieldName='reportTemplate'
                          placeholder={formatMessage({
                            id: 'Select Template',
                          })}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: 'Choose an existing predefined report template to automatically populate report details for generation.',
                          })}
                          options={templateOptions}
                          isStarRequired={false}
                          isTooltipForModal={true}
                        />

                        <p className='text-primary m-0'>
                          {formatMessage({
                            id: ' A report will be generated in CSV file format for all users data/activity in this template.',
                          })}
                        </p>
                      </Col>

                      <Col md={6}>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({ id: 'Report Name' })}
                          fieldName='reportName'
                          formik={formik}
                          placeholder={formatMessage({ id: 'Enter Report Name' })}
                          toolTipText={formatMessage({
                            id: 'Enter a unique name for this report to easily identify it later.',
                          })}
                          isStarRequired={true}
                          isTooltipForModal={true}
                        />
                      </Col>
                    </Row>

                    <div
                      style={{ background: 'rgb(71 118 230 / 4%)' }}
                      className='p-4 rounded-2 mt-4'
                    >
                      <h2 className='font-size-16 m-0'>
                        {formatMessage({ id: 'Report Information & Details in selected Template' })}
                      </h2>

                      <Row className='mt-4'>
                        <Col md={8}>
                          <TextInput
                            fieldType='text'
                            label={formatMessage({
                              id: 'I want to generate the report of below activity type (e.g., file access, upload, modification)',
                            })}
                            fieldName='AccessActivity'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Access Activities' })}
                            toolTipText={formatMessage({
                              id: 'Select the type of activities to include in the report, such as file access, upload, or modification.',
                            })}
                            isStarRequired={true}
                            isTooltipForModal={true}
                            isDisabled={true}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                            {formatMessage({ id: 'For below actions' })}{' '}
                            <ToolTipUIForModal
                              tooltipText={formatMessage({
                                id: 'Choose the specific actions you want to report on, such as record access or permission changes.',
                              })}
                            />
                          </label>

                          <div className='d-flex w-100  align-items-center'>
                            <div className='d-flex align-items-center gap-2'>
                              <TextInput
                                fieldName={'recordAccess'}
                                formik={formik}
                                customText={formatMessage({ id: 'Record Access' })}
                                placeholder=''
                                fieldType={'checkbox'}
                                isCheckbox={true}
                                isWidthNotRequired={true}
                                isTooltipNotRequired={true}
                                isDisabled={true}
                                checkboxSize='24'
                              />
                            </div>

                            <div className='d-flex align-items-center gap-2'>
                              <TextInput
                                fieldName={'permissionChanges'}
                                formik={formik}
                                customText={formatMessage({ id: 'Permission Changes' })}
                                placeholder=''
                                fieldType={'checkbox'}
                                value={scheduleReportData?.permissionChanges}
                                isCheckbox={true}
                                isWidthNotRequired={true}
                                isTooltipNotRequired={true}
                                isDisabled={true}
                                checkboxSize='24'
                              />
                            </div>

                            <div className='d-flex align-items-center gap-2'>
                              <TextInput
                                fieldName={'unauthorized'}
                                formik={formik}
                                customText={formatMessage({
                                  id: 'Unauthorized Access Attempts',
                                })}
                                placeholder=''
                                fieldType={'checkbox'}
                                isCheckbox={true}
                                isWidthNotRequired={true}
                                isTooltipNotRequired={true}
                                isDisabled={true}
                                checkboxSize='24'
                              />
                            </div>

                            <div className='d-flex align-items-center gap-2'>
                              <TextInput
                                fieldName={'linkSharing'}
                                formik={formik}
                                customText={formatMessage({ id: 'Link Sharing' })}
                                placeholder=''
                                fieldType={'checkbox'}
                                isCheckbox={true}
                                isWidthNotRequired={true}
                                isTooltipNotRequired={true}
                                isDisabled={true}
                                checkboxSize='24'
                              />
                            </div>

                            <div className='d-flex align-items-center gap-2'>
                              <TextInput
                                fieldName={'geoLocation'}
                                formik={formik}
                                customText={formatMessage({ id: 'Geolocation of Access' })}
                                placeholder=''
                                fieldType={'checkbox'}
                                isCheckbox={true}
                                isWidthNotRequired={true}
                                isTooltipNotRequired={true}
                                isDisabled={true}
                                checkboxSize='24'
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className='mt-4'>
                        <Col md={8}>
                          <TagifyInput
                            label={formatMessage({ id: 'Of below Users' })}
                            placeholder={formatMessage({ id: 'Add User' })}
                            tooltip={formatMessage({
                              id: 'Select users whose activities should be included in this report.',
                            })}
                            fieldName='validUsers'
                            formik={formik}
                            isDisabled={true}
                          />
                        </Col>
                      </Row>

                      <Row className='mt-4'>
                        <Col md={6}>
                          <TagInput
                            label={formatMessage({ id: 'From these departments' })}
                            initialOptions={[]}
                            placeholder={formatMessage({ id: 'Add Department' })}
                            tooltip={formatMessage({
                              id: 'Select departments to filter activities and records belonging to those specific departments.',
                            })}
                            fieldName='departmentLists'
                            formik={formik}
                            disabled={true}
                          />
                        </Col>

                        <Col md={6}>
                          <TagInput
                            label={formatMessage({ id: 'From these categories' })}
                            initialOptions={[]}
                            placeholder={formatMessage({ id: 'Add Category' })}
                            tooltip={formatMessage({
                              id: 'Filter records by categories to include specific document groups in the report.',
                            })}
                            fieldName='categoryLists'
                            formik={formik}
                            disabled={true}
                          />
                        </Col>
                      </Row>

                      <div className='d-md-flex flex-md-column mt-4'>
                        <div className='d-flex form-label  text-dark font-size-13'>
                          {formatMessage({ id: 'In this output format' })}{' '}
                          <ToolTipUIForModal
                            tooltipText={formatMessage({
                              id: 'Choose the file format for exporting the report, either PDF or XLS.',
                            })}
                          />
                        </div>
                        <div className='d-flex align-items-center gap-4'>
                          {/* <div className='d-flex align-items-center gap-1'>
                            <input
                              type='radio'
                              id='employee-yes'
                              name='format'
                              value='PDF'
                              onChange={formik.handleChange}
                              checked={formik?.values?.format === 'PDF'}
                              width={48}
                              height={48}
                              disabled={true}
                            />
                            <label htmlFor='employee-yes'>{formatMessage({id: 'PDF'})}</label>
                          </div> */}
                          <div className='d-flex align-items-center gap-1'>
                            <input
                              type='radio'
                              id='employee-no'
                              name='format'
                              onChange={formik.handleChange}
                              checked={true}
                              value='XLS'
                              width={48}
                              height={48}
                              disabled={true}
                            />
                            <label htmlFor='xlsx'>{formatMessage({ id: 'XLSX' })}</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className='font-size-16 mt-4 mb-4'>
                      {formatMessage({ id: 'Report Period' })}
                    </h2>

                    <Row>
                      <Col md={8}>
                        <div className='d-flex flex-row mb-2'>
                          <div>For this period</div>
                          <ToolTipUIForModal
                            tooltipText={formatMessage({
                              id: 'Based on the first dropdown, the date range will be set. In case the user choses Custom value, user will be asked to chose their own custom range.',
                            })}
                          />
                        </div>

                        <div className='d-flex justify-content-between mb-5'>
                          <select
                            className='form-select h-40px font-size-12 w-200px border font-weight-500 bg-white rounded-end-0 cursor-pointer'
                            onChange={(e) => setSelectedOption(e.target.value)}
                          >
                            <option value='MTD'>{formatMessage({ id: 'Month-to-Date' })}</option>
                            <option value='QTD'>{formatMessage({ id: 'Quarter-to-Date' })}</option>
                            <option value='CYTD'>
                              {formatMessage({ id: 'Calendar-Year-to-Date' })}
                            </option>
                            <option value='FYTD'>
                              {formatMessage({ id: 'Financial-Year-to-Date' })}
                            </option>
                            <option value='custom'>{formatMessage({ id: 'Custom' })}</option>
                          </select>

                          <DatePicker
                            selected={startDate}
                            onChange={(dates: [Date | null, Date | null] | null) => {
                              if (dates) {
                                setDateRange([dates[0], dates[1]])
                              }
                            }}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            className='form-control h-40px font-size-12 w-200px  border ms-2'
                            placeholderText='Select Date Range'
                            disabled={selectedOption !== 'custom'}
                          />
                        </div>
                      </Col>
                    </Row>
                  </>
                  <Modal.Footer>
                    <div className='d-md-flex w-100 justify-content-end  px-0 gap-3'>
                      <button type='button' className='btn bg-light custom-button-property text-muted w-auto'>
                        {formatMessage({ id: 'Cancel' })}
                      </button>

                      <button
                        type='submit'
                        className='btn bg-primary custom-button-property text-white w-auto'
                        disabled={!formik.isValid || isLoading || !startDate || !endDate}
                        onClick={formik.submitForm}
                      >
                        {!isLoading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Generate Report' })}
                          </span>
                        ) : (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </Modal.Footer>
                </>
              )
            }}
          </Formik>
        </div>
      </div>
    </>
  )
}

export default GenerateReportExistingTemplate
